const connectionMessage = 'The signing service could not be reached. Check your connection and try again.';
const timeoutMessage = 'The signing service did not respond in time. Check your connection and try again.';
/** Keep the deadline and fallback active until the complete response body is read. */
function requestRecipientRead(primaryBaseUrl, fallbackBaseUrl, path, readBody, { signal, fallbackDelayMs = 2_500, timeoutMs = 30_000 }) {
    const primary = primaryBaseUrl.replace(/\/$/, '');
    const fallback = fallbackBaseUrl?.replace(/\/$/, '');
    const hasFallback = Boolean(fallback && fallback !== primary);
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(signal.reason ?? new DOMException('The request was cancelled.', 'AbortError'));
            return;
        }
        const controllers = new Map();
        let settled = false;
        let terminalBaseUrl;
        let terminalStatus;
        let fallbackStarted = false;
        let pending = 0;
        let fallbackTimer;
        let timeoutTimer;
        const finish = (error, result) => {
            if (settled)
                return;
            settled = true;
            clearTimeout(fallbackTimer);
            clearTimeout(timeoutTimer);
            signal?.removeEventListener('abort', cancel);
            for (const [baseUrl, controller] of controllers) {
                if (baseUrl !== result?.baseUrl)
                    controller.abort();
            }
            if (result)
                resolve(result);
            else
                reject(error);
        };
        const cancel = () => finish(signal?.reason ?? new DOMException('The request was cancelled.', 'AbortError'));
        const startFallback = () => {
            if (settled || terminalBaseUrl || !hasFallback || fallbackStarted)
                return;
            fallbackStarted = true;
            clearTimeout(fallbackTimer);
            start(fallback);
        };
        const start = (baseUrl) => {
            const controller = new AbortController();
            controllers.set(baseUrl, controller);
            pending += 1;
            void (async () => {
                try {
                    const response = await fetch(`${baseUrl}${path}`, {
                        cache: 'no-store',
                        signal: controller.signal,
                    });
                    if (settled || (terminalBaseUrl && terminalBaseUrl !== baseUrl))
                        return;
                    // A terminal link/access response is authoritative on either route.
                    // Do not route around it, even while reading its error body.
                    if (response.status >= 400 && response.status < 500) {
                        terminalBaseUrl = baseUrl;
                        terminalStatus = response.status;
                        clearTimeout(fallbackTimer);
                        for (const [otherUrl, otherController] of controllers) {
                            if (otherUrl !== baseUrl)
                                otherController.abort();
                        }
                        const payload = await response.json().catch(() => ({}));
                        finish(new Error(payload.error || `Signing service returned ${response.status}.`));
                        return;
                    }
                    if (!response.ok) {
                        // A server error can also have a stalled body. Recover as soon as
                        // its headers arrive, rather than waiting for its JSON error.
                        startFallback();
                        const payload = await response.json().catch(() => ({}));
                        throw new Error(payload?.error || `Signing service returned ${response.status}.`);
                    }
                    const payload = await readBody(response);
                    if (settled || (terminalBaseUrl && terminalBaseUrl !== baseUrl))
                        return;
                    finish(undefined, { payload, baseUrl });
                }
                catch (reason) {
                    if (settled || (terminalBaseUrl && terminalBaseUrl !== baseUrl))
                        return;
                    pending -= 1;
                    const error = reason instanceof TypeError ? new Error(connectionMessage) : reason;
                    if (hasFallback && !fallbackStarted)
                        startFallback();
                    else if (pending === 0)
                        finish(error);
                }
            })();
        };
        signal?.addEventListener('abort', cancel, { once: true });
        timeoutTimer = setTimeout(() => finish(new Error(terminalStatus
            ? `Signing service returned ${terminalStatus}.`
            : timeoutMessage)), timeoutMs);
        if (hasFallback)
            fallbackTimer = setTimeout(startFallback, fallbackDelayMs);
        start(primary);
    });
}
/** Recover a stalled, read-only session lookup through the other trusted API route. */
export function requestRecipientSession(primaryBaseUrl, fallbackBaseUrl, path, options = {}) {
    return requestRecipientRead(primaryBaseUrl, fallbackBaseUrl, path, (response) => response.json(), options);
}
/** Download a whole PDF with read-only route recovery, including a stalled body. */
export function requestRecipientDocument(primaryBaseUrl, fallbackBaseUrl, path, options = {}) {
    // Completed PDFs can grow beyond the upload limit as signatures are added.
    // Do not reject legitimate documents with an upload-size-based download cap.
    return requestRecipientRead(primaryBaseUrl, fallbackBaseUrl, path, (response) => response.arrayBuffer(), {
        timeoutMs: 90_000,
        ...options,
    });
}
